import { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { Save } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import FormErrorSummary from '@/components/FormErrorSummary/FormErrorSummary';
import CpdActionButton from '@/components/ui/CpdActionButton';
import CpdTextField from '@/components/ui/CpdTextField';
import CpdDropdown from '@/components/ui/CpdDropdown';
import { FacilityGroupsApi } from '@/api/clients/facility-groups/facility-groups.api';
import { useGetFacilityGroups } from '@/api/hooks/facility-groups/useGetFacilityGroups';
import useFormValidation from '@/hooks/useFormValidation';

const FacilityGroupDetailsSection = ({
  facilityGroup,
  organizationId,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { facilityGroups: availableParents } = useGetFacilityGroups(
    organizationId,
  );
  const {
    fieldErrors,
    generalErrors,
    setBackendErrors,
    clearAllErrors,
  } = useFormValidation();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parent_id: '',
  });

  useEffect(() => {
    if (facilityGroup) {
      setFormData({
        name: facilityGroup.name || '',
        description: facilityGroup.description || '',
        parent_id: facilityGroup.parent_id || '',
      });
    }
  }, [facilityGroup]);

  const filteredParents = useMemo(() => {
    if (!facilityGroup) return availableParents;
    return availableParents.filter((p) => p.id !== facilityGroup.id);
  }, [availableParents, facilityGroup]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleParentChange = (e) => {
    setFormData((prev) => ({ ...prev, parent_id: e.target.value }));
  };

  const handleSave = async () => {
    clearAllErrors();
    setLoading(true);
    try {
      const dataToSave = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        parent_id: formData.parent_id || null,
      };
      await FacilityGroupsApi.updateFacilityGroup(
        organizationId,
        facilityGroup.id,
        dataToSave,
      );
      onUpdate?.();
    } catch (err) {
      setBackendErrors(err.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const hasChanges =
    facilityGroup &&
    (formData.name !== (facilityGroup.name || '') ||
      formData.description !== (facilityGroup.description || '') ||
      formData.parent_id !== (facilityGroup.parent_id || ''));

  return (
    <Box>
      <Typography variant='h6' gutterBottom sx={{ mb: 2 }}>
        {t('facilityGroups.details.title')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormErrorSummary errors={generalErrors} />

        <CpdTextField
          name='name'
          label={t('facilityGroups.form.name')}
          value={formData.name}
          onChange={handleChange}
          required
          disabled={loading}
          error={!!fieldErrors.name}
          helperText={fieldErrors.name}
          inputProps={{ maxLength: 255 }}
        />

        <CpdTextField
          name='description'
          label={t('facilityGroups.form.description')}
          value={formData.description}
          onChange={handleChange}
          disabled={loading}
          error={!!fieldErrors.description}
          helperText={fieldErrors.description}
          multiline
          rows={3}
        />

        <CpdDropdown
          label={t('facilityGroups.form.parentGroup')}
          options={filteredParents}
          value={formData.parent_id}
          onChange={handleParentChange}
          disabled={loading}
          error={!!fieldErrors.parent_id}
          helperText={fieldErrors.parent_id}
          placeholder={t('facilityGroups.form.noParent')}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.id}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <CpdActionButton
            onClick={handleSave}
            loading={loading}
            disabled={!hasChanges || !formData.name.trim()}
            startIcon={<Save />}>
            {t('common.buttons.saveChanges')}
          </CpdActionButton>
        </Box>
      </Box>

      <Divider sx={{ my: 3, mt: 4 }} />
    </Box>
  );
};

export default FacilityGroupDetailsSection;
